import { Alert, Box, FormControl, TextField, useTheme } from "@mui/material";
import React from "react";

import { useAuth } from "@/providers";
import { RobotoLoadingButton, RobotoTypography } from "@/shared/components";
import { useCreateSecretMutation } from "@/shared/domain/secrets/hooks/useCreateSecretMutation";
import { RobotoDomainException } from "@/shared/http";

export const NewSecretForm: React.FC = () => {
  const [secretName, setSecretName] = React.useState("");
  const [secretValue, setSecretValue] = React.useState("");
  const [secretNameError, setSecretNameError] = React.useState(false);
  const [secretValueError, setSecretValueError] = React.useState(false);

  const theme = useTheme();

  const createSecretMutation = useCreateSecretMutation();

  const { currentOrganization } = useAuth();

  const handleCreateSecret = () => {
    if (secretName.length === 0 || !validateAwsSecretName(secretName)) {
      setSecretNameError(true);
      return;
    } else if (secretValue.trim().length === 0) {
      setSecretValueError(true);
      return;
    } else {
      setSecretNameError(false);
      setSecretValueError(false);
    }

    createSecretMutation.mutate(
      {
        secretName,
        secretValue,
        orgId: currentOrganization?.org_id || "",
      },
      {
        onSuccess: () => {
          setSecretName("");
          setSecretValue("");
        },
      },
    );
  };

  return (
    <>
      <RobotoTypography
        variant="body1"
        sx={{
          marginBottom: theme.spacing(2),
        }}
      >
        Create a new secret
      </RobotoTypography>
      <FormControl
        sx={{ display: "flex", width: "100%", marginBottom: theme.spacing(4) }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: theme.spacing(2),
            width: "100%",
          }}
        >
          <TextField
            autoComplete="off"
            label={"Secret Name"}
            value={secretName}
            onChange={(event) => {
              setSecretName(event.target.value);
              setSecretNameError(false);
            }}
            size="small"
            helperText={
              secretNameError
                ? "Secret names should be between 1 and 512 characters and only contain alphanumeric characters and the following symbols: / _ + = . @ -"
                : "Give your secret a name, e.g. Slack_API_Key"
            }
            error={secretNameError}
            sx={{ flex: 1 }}
          />
          <TextField
            autoComplete="off"
            inputProps={{}}
            type="text"
            label={"Secret Value"}
            value={secretValue}
            onKeyDown={(event) => {
              if (event.key.toLowerCase() === "enter") {
                event.preventDefault();
                void handleCreateSecret();
              }
            }}
            onChange={(event) => {
              setSecretValue(event.target.value);
              setSecretValueError(false);
            }}
            size="small"
            helperText={
              secretValueError
                ? "Secret value is required"
                : "The value of the secret"
            }
            sx={{ flex: 1 }}
            //ensure this field is not captured by PostHog
            className="ph-no-capture"
            error={secretValueError}
          />
          <RobotoLoadingButton
            eventName={"CreateSecretClicked"}
            variant="contained"
            sx={{ width: "100px", marginTop: "2px" }}
            onClick={() => void handleCreateSecret()}
            loading={createSecretMutation.isPending}
          >
            Create
          </RobotoLoadingButton>
        </Box>
      </FormControl>
      {createSecretMutation.isError && (
        <Alert
          severity="error"
          sx={{ marginTop: theme.spacing(2) }}
          onClose={() => createSecretMutation.reset()}
        >
          {createSecretMutation.error instanceof RobotoDomainException
            ? createSecretMutation.error.message
            : "Failed to create secret. Please try again."}
        </Alert>
      )}
    </>
  );
};

const validateAwsSecretName = (name: string) => {
  if (name.length < 1 || name.length > 512) {
    return false;
  }

  // Must match only allowed characters: A–Z a–z 0–9 / _ + = . @ -
  const allowedPattern = /^[A-Za-z0-9/_+=.@-]+$/;
  if (!allowedPattern.test(name)) {
    return false;
  }

  // Cannot end with a slash
  if (name.endsWith("/")) {
    return false;
  }

  // Cannot contain double slashes
  if (name.includes("//")) {
    return false;
  }

  return true;
};
